// Initialize Sentry FIRST - must be imported before any other modules
import '../instrument.js';

import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { supabaseConfigurationError } from './lib/supabase';
import { reloadForStaleChunk } from './lib/lazyWithReload';

// A tab left open across a deploy may request chunks the new deploy no longer
// serves. Vite fires this for failed dynamic imports / preloads; reload once
// (guarded against loops) to pick up the current build.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadForStaleChunk()) {
    event.preventDefault();
  }
});

// Detect Safari and add class to html element for CSS targeting
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
if (isSafari) {
  document.documentElement.classList.add('is-safari');
}

const container = document.getElementById('root')!;
const root = createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary
  onCaughtError: Sentry.reactErrorHandler(),
  // Callback called when React automatically recovers from errors
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      {supabaseConfigurationError ? (
        <main className="min-h-screen bg-background px-6 py-16 text-foreground">
          <div role="alert" className="mx-auto max-w-xl space-y-3 rounded-md border border-border p-6">
            <h1 className="text-xl font-semibold">Preview configuration required</h1>
            <p className="text-sm">{supabaseConfigurationError}</p>
          </div>
        </main>
      ) : <App />}
    </ErrorBoundary>
  </React.StrictMode>
);
